import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import api from './api';
import { getPhotos } from './photoDB';
import BackButton from './BackButton';

/** Maximum payload per batch (40 MB of actual binary) */
const BATCH_SIZE_LIMIT = 40 * 1024 * 1024;

/**
 * Split an array of Blobs into batches whose combined size stays under
 * BATCH_SIZE_LIMIT so no single request exceeds the server's body limit.
 */
function batchBySize(blobs) {
  const batches = [];
  let currentBatch = [];
  let currentSize = 0;

  for (const blob of blobs) {
    const size = blob.size;
    if (currentBatch.length > 0 && currentSize + size > BATCH_SIZE_LIMIT) {
      batches.push(currentBatch);
      currentBatch = [blob];
      currentSize = size;
    } else {
      currentBatch.push(blob);
      currentSize += size;
    }
  }
  if (currentBatch.length > 0) batches.push(currentBatch);
  return batches;
}

/**
 * Retry an async function up to maxAttempts times with exponential back-off.
 * Does NOT retry 401 (session expired - retrying can't help) or 502
 * (partial upload - a blind retry would duplicate the photos that DID
 * reach Asana/Azure).
 */
async function withRetry(fn, maxAttempts = 3) {
  const delays = [1000, 2000];
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const status = err.response?.status;
      const retryable = status !== 401 && status !== 502;
      if (retryable && attempt < maxAttempts - 1) {
        await new Promise(res => setTimeout(res, delays[attempt] ?? 2000));
      } else {
        throw err;
      }
    }
  }
}

/**
 * Archive a batch of Blobs to Azure Blob Storage via multipart upload.
 * Dealer identity travels in the session token, not the form.
 */
async function uploadPhotosToAzure(blobs) {
  const formData = new FormData();
  blobs.forEach((blob, i) => {
    formData.append('photos', blob, `photo_${i + 1}.jpg`);
  });
  const response = await api.post('/api/store-photos-azure', formData);
  return response.data;
}

function PhotoConfirm() {
  /*
  PhotoConfirm lets users review their selected photos, add comments, then
  submit concurrently to Azure Blob Storage (full-resolution archive for
  the art team) and Asana (workflow attachment).

  Large payloads are split into size-bounded batches; each batch is retried
  with back-off on transient failures. On success, navigates to /thank-you
  with allPhotoKeys so ThankYou can wipe the session photos from IndexedDB.
  */
  const location = useLocation();
  const navigate = useNavigate();

  // Full session key list - used for IndexedDB cleanup on success
  const photoKeys = useMemo(() => location.state?.photoKeys || [], [location.state]);
  // The subset the user selected in PhotoGallery
  const selectedPhotoKeys = useMemo(() => location.state?.selectedPhotoKeys || [], [location.state]);

  const [selectedBlobs, setSelectedBlobs] = useState([]);
  const [previewURLs, setPreviewURLs] = useState([]);
  const objectURLsRef = useRef([]);
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [transferMessage, setTransferMessage] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  // Load selected Blobs from IndexedDB once; create object URLs for preview.
  // The same blobs are reused at submit time — no second IndexedDB round-trip.
  useEffect(() => {
    async function fetchSelectedPhotos() {
      const blobs = await getPhotos(selectedPhotoKeys);
      const urls = blobs.map(blob => URL.createObjectURL(blob));
      objectURLsRef.current = urls;
      setSelectedBlobs(blobs);
      setPreviewURLs(urls);
    }
    if (selectedPhotoKeys.length) fetchSelectedPhotos();
    return () => {
      objectURLsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, [selectedPhotoKeys]);

  const handleConfirm = async () => {
    if (selectedBlobs.length === 0) return;

    setIsTransferring(true);
    setError('');

    try {
      const batches = batchBySize(selectedBlobs);

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const isFirst = i === 0;
        const batchLabel = batches.length > 1 ? ` (batch ${i + 1} of ${batches.length})` : '';
        setTransferMessage(`Transferring photos${batchLabel}...`);

        // Build multipart form for Asana attachment.
        const asanaForm = new FormData();
        batch.forEach((blob, j) => asanaForm.append('photos', blob, `photo_${j + 1}.jpg`));
        if (isFirst) asanaForm.append('comments', comments);

        // Azure archive and Asana attachment run concurrently per batch.
        await withRetry(() => Promise.all([
          uploadPhotosToAzure(batch),
          api.post('/api/store-photos', asanaForm),
        ]));
      }

      // Pass the full session key list so ThankYou can clear IndexedDB.
      navigate('/thank-you', { state: { allPhotoKeys: photoKeys, support: false } });
    } catch (err) {
      const status = err.response?.status;
      const serverMessage = err.response?.data?.message || err.response?.data?.error;
      if (status === 401) {
        setSessionExpired(true);
        setError('Your session has expired. Please re-enter your Dealer ID - your photos are saved and will still be here.');
      } else if (status === 502 && serverMessage) {
        setError(serverMessage);
      } else {
        setError('An error occurred while sending photos. Please try again.');
      }
      setIsTransferring(false);
      setTransferMessage('');
    }
  };

  const handleBack = () => {
    navigate('/photo-gallery', {
      state: { photoKeys, selectedPhotoKeys },
    });
  };

  return (
    <div className="PhotoConfirm">
      <h1 className="PhotoConfirm-title">Confirm Your Selected Photos</h1>
      <div className="PhotoConfirm-grid">
        {previewURLs.map((url, index) => (
          <div key={index} className="PhotoConfirm-item">
            <img src={url} alt={`Selected ${index + 1}`} className="PhotoConfirm-image" />
          </div>
        ))}
      </div>
      <textarea
        className="PhotoConfirm-comments"
        placeholder="Include any comments or questions you'd like to submit with your photos..."
        value={comments}
        onChange={e => setComments(e.target.value)}
        rows={3}
        maxLength={2000}
        style={{ width: '100%', margin: '20px 0', borderRadius: '8px', padding: '12px', fontSize: '16px', border: '1px solid #ccc' }}
      />
      {isTransferring && (
        <div style={{ color: '#1976d2', marginBottom: '12px', fontWeight: 500 }}>
          {transferMessage}
        </div>
      )}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>
          {error}
        </div>
      )}
      <div className="PhotoConfirm-actions">
        <button className="App-button" onClick={handleBack} disabled={isTransferring}>
          Back to Gallery
        </button>
        {sessionExpired ? (
          <button className="App-button" onClick={() => navigate('/dealer-id')}>
            Re-enter Dealer ID
          </button>
        ) : (
          <button className="App-button" onClick={handleConfirm} disabled={isTransferring || selectedBlobs.length === 0}>
            Confirm Photos
          </button>
        )}
      </div>
      <BackButton />
    </div>
  );
}

export default PhotoConfirm;
